import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { OfferBuilderErrorState } from '@/enterprise/components/OfferBuilder/OfferBuilderErrorState';
import { OfferReviewLayoutSkeleton } from '@/enterprise/components/OfferBuilder/OfferReview/OfferReviewLayout';
import { useDataDog } from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useReadOnlyUser, useSunspotUser } from '@/utils/useRestrictions';
import { AcquisitionOfferComponent } from './AcquisitionOfferCore';

/**
 * Tracks errors when loading the acquisition offer review page and displays error UI with recovery options.
 */
const AcquisitionOfferReviewError = () => {
  const { addError } = useDataDog();

  useEffectOnce(() => {
    addError('Acquisition Offer Review Error');
  });

  return <OfferBuilderErrorState />;
};

interface AcquisitionRestrictedUserRedirectProps {
  readOnlyUser: boolean;
  sunspotUser: boolean;
}

/**
 * Records a Datadog RUM action then redirects.
 */
const AcquisitionRestrictedUserRedirect = ({ readOnlyUser, sunspotUser }: AcquisitionRestrictedUserRedirectProps) => {
  const { addAction } = useDataDog();
  const [searchParams] = useSearchParams();
  const acquisitionCorrelationId = searchParams.get('correlationId') ?? undefined;
  const urlReferrer = searchParams.get('referrer') ?? undefined;

  useEffectOnce(() => {
    addAction('enterprise::acquisition-offer::restricted-user::redirected', {
      readOnlyUser,
      sunspotUser,
      ...(acquisitionCorrelationId && { acquisitionCorrelationId }),
      ...(urlReferrer && { urlReferrer }),
    });
  });

  // navigate user to customers page removing any acquisition offer query params
  return <Navigate to={asEnterpriseLink('customers')} replace />;
};

/**
 * Checks read-only status and sunspot user status and either redirects or renders the offer flow with its own
 * loading boundary.
 */
const AcquisitionOfferGuard = () => {
  const { t } = useTranslation();
  const readOnlyUser = useReadOnlyUser();
  const sunspotUser = useSunspotUser();

  if (readOnlyUser || sunspotUser) {
    return <AcquisitionRestrictedUserRedirect readOnlyUser={readOnlyUser} sunspotUser={sunspotUser} />;
  }

  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="flex flex-col gap-6 pb-10">
          <h1 className="m-0 text-4xl">{t('acquisition.oneClickEarlyPay')}</h1>
          <OfferReviewLayoutSkeleton />
        </div>
      )}
      ErrorComponent={AcquisitionOfferReviewError}
    >
      <AcquisitionOfferComponent />
    </QueryBoundaries>
  );
};

/**
 * Entry point for the acquisition one-time price discovery offer flow.
 *
 * The outer QueryBoundaries has no fallback UI so the read-only check in
 * AcquisitionOfferGuard resolves silently — read-only users are redirected
 * before the skeleton loading state is ever shown.
 */
const AcquisitionOfferReview = () => (
  <QueryBoundaries>
    <AcquisitionOfferGuard />
  </QueryBoundaries>
);

export default AcquisitionOfferReview;
